import { DeviceItem, useLocaleStore } from '@/stores'
import { rgbToHex } from '@/utils/color'
import {
  BaseCurrentLightEffect,
  BaseLightEffect,
  hasLightingEditableField,
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_TARGET,
  LightingEffectDefinition,
} from '@rapoo/device-sdk'
import { computed, ref } from 'vue'

export function useLightBand(itemRef: { value: DeviceItem }) {
  const { t } = useLocaleStore()
  const device = computed(() => itemRef.value.original)

  const effect = ref<BaseCurrentLightEffect | null>(null)
  const initCurrentLightEffect = async () => {
    const lighting = device.value.features.baseLighting
    if (lighting) {
      effect.value = await lighting.getCurrentEffect(LIGHTING_TARGET.BaseLightBand)
    }
  }

  const lightEffects = ref<LightingEffectDefinition[]>([])
  const initLightEffects = async () => {
    const lighting = device.value.features.baseLighting
    if (lighting) {
      // 底座灯效元数据使用统一 lighting.effects，按灯带目标过滤才能匹配当前 modeId。
      lightEffects.value = await lighting.getEffects(LIGHTING_TARGET.BaseLightBand)
    }
  }

  const currentEffectDefinition = computed(() => {
    if (!effect.value) return null
    return lightEffects.value.find((item) => item.id === effect.value?.modeId) ?? null
  })

  const effectName = computed(() => currentEffectDefinition.value?.i18nKey ?? null)

  const canEditColor = computed(() => {
    return hasLightingEditableField(currentEffectDefinition.value, LIGHTING_EDITABLE_FIELD.Color)
  })

  const canEditSpeed = computed(() => {
    return hasLightingEditableField(currentEffectDefinition.value, LIGHTING_EDITABLE_FIELD.Speed)
  })

  const canEditBrightness = computed(() => {
    return hasLightingEditableField(
      currentEffectDefinition.value,
      LIGHTING_EDITABLE_FIELD.Brightness,
    )
  })

  const baseLight = ref<BaseLightEffect | null>(null)
  const initLightBand = async (mode: number) => {
    const lighting = device.value.features.baseLighting
    if (lighting) {
      baseLight.value = await lighting.getLightEffect(mode, LIGHTING_TARGET.BaseLightBand)
    }
  }

  const initLightBandData = async () => {
    await Promise.all([initCurrentLightEffect(), initLightEffects()])
    if (effect.value) {
      const currentModeId = effect.value.modeId
      if (!currentModeId) {
        return
      }

      const matchedEffect = lightEffects.value.find((item) => item.id === currentModeId)
      if (!matchedEffect) {
        return
      }

      await initLightBand(matchedEffect.id)
    }
  }

  const lightBandData = computed(() => {
    if (device.value.manifest.type !== 'base') {
      return null
    }
    const data: { name: string; value: string }[] = []

    if (effectName.value) {
      let name = t(effectName.value)
      if (canEditColor.value && baseLight.value?.color) {
        const [r, g, b] = baseLight.value.color
        name += ` (${rgbToHex({ r, g, b })})`
      }
      // 灯光模式
      data.push({
        name: t('home.overview.lightBand.lightMode'),
        value: name,
      })
    }

    const chargeLightLabel =
      effect.value?.chargeLightDisplayMode === 'ambient'
        ? t('home.overview.lightBand.ambientLight')
        : effect.value?.chargeLightDisplayMode === 'charging'
          ? t('home.overview.lightBand.chargeLight')
          : null
    if (chargeLightLabel) {
      data.push({
        name: t('home.overview.lightBand.chargeState'),
        value: chargeLightLabel,
      })
    }

    if (canEditSpeed.value && baseLight.value?.frequency !== undefined) {
      // 灯光速度
      data.push({
        name: t('home.overview.lightBand.speed'),
        value: `${baseLight.value.frequency}%`,
      })
    }

    if (canEditBrightness.value && baseLight.value?.brightness !== undefined) {
      // 灯光亮度
      data.push({
        name: t('home.overview.lightBand.brightness'),
        value: `${baseLight.value.brightness}%`,
      })
    }

    if (!data.length) return null

    return {
      title: t('home.overview.lightBand.title'),
      data,
    }
  })

  return {
    lightBandData,
    initLightBandData,
  }
}
